import { getTextFromHtml } from '../utils/htmlUtils';

/**
 * The minimum an interaction has to expose for us to tell whether it was an email and what its
 * subject was. Deliberately structural: the dashboard's `HistoryData` and the extension sidepanel's
 * `HistoryItem` describe the same rows with different wrappers, and both satisfy this.
 */
export type EmailSubjectInteraction = {
  interactionAction?: string | null;
  subject?: string | null;
  /** Owner of the interaction. The contact timeline is team-wide; threading is per-user. */
  userId?: string | null;
};

const EMAIL_ACTIONS = new Set(['email_sent', 'email_received']);

/**
 * Subject of the most recent email THE CURRENT USER exchanged with a contact — sent OR received —
 * as plain text, or null when there is none.
 *
 * This is what gates the composer's "Reply in thread" toggle and fills its banner, so it must agree
 * with what the server actually threads onto: `contactUserInteractionGetLastEmail`, which takes the
 * latest `email_sent`/`email_received` row **scoped to `user_id`**.
 *
 * The `userId` scoping is load-bearing, not defensive. The contact timeline both surfaces render is
 * TEAM-wide (`contactInteractionFetch` has no user predicate), so without it a teammate's email
 * would offer the current rep a toggle the server cannot honour: the send would arrive with no
 * subject (the field is hidden while threading) and be rejected as "Missing subject for email" —
 * or, if the rep happens to have an older email of their own, thread into that one while the banner
 * named the teammate's.
 *
 * Note this is NOT the same set as the removed "Reply to existing thread" subject dropdown, which
 * read `email_sent` only — it couldn't see the inbound message being replied to.
 *
 * @param interactions Timeline rows in ascending date order (oldest first), as both surfaces render them.
 * @param currentUserId The signed-in user. Rows belonging to anyone else are skipped.
 */
export function getPreviousEmailSubject(
  interactions: readonly EmailSubjectInteraction[] | null | undefined,
  currentUserId: string | null | undefined,
): string | null {
  if (interactions == null || currentUserId == null) return null;
  for (let i = interactions.length - 1; i >= 0; i--) {
    const interaction = interactions[i];
    if (interaction?.interactionAction == null || !EMAIL_ACTIONS.has(interaction.interactionAction)) continue;
    if (interaction.userId !== currentUserId) continue;
    // Stop at the FIRST email found scanning backwards — never skip past a subject-less one to an
    // older email. The server always threads onto the latest email, so falling back to an earlier
    // subject would show the rep one thread while the send landed in another. A subject-less latest
    // email simply means no toggle, and the rep writes a subject for a fresh thread.
    //
    // Subjects are stored as plain text on send, but older rows (and anything that round-tripped
    // through the editor) can still carry `<p>`-wrapped HTML — flatten before showing it.
    const subject = getTextFromHtml(interaction.subject ?? '').trim();
    return subject.length > 0 ? subject : null;
  }
  return null;
}
